import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import API, { formatApiError } from "../api/client";

const AuthContext = createContext(null);

const HUB_ORIGINS = [
  "https://sso-engine-1.preview.emergentagent.com",
  "https://controlhub.blackscott.co.za",
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=not auth, object=auth
  const [loading, setLoading] = useState(true);
  const [embedded, setEmbedded] = useState(false);
  const ssoProcessed = useRef(false);

  // Process SSO token (from URL or postMessage)
  const processSSOToken = useCallback(async (token) => {
    if (ssoProcessed.current) return;
    ssoProcessed.current = true;
    try {
      const { data } = await API.post("/sso/accept", { sso_token: token });
      setUser(data);
      if (data.tenant_branding) {
        try { sessionStorage.setItem("tenant_branding", JSON.stringify(data.tenant_branding)); } catch {}
      }
      if (data.tenant_name) {
        try { sessionStorage.setItem("tenant_name", data.tenant_name); } catch {}
      }
      setLoading(false);
      return true;
    } catch (err) {
      console.error("SSO accept failed:", err.response?.data?.detail || err.message);
      ssoProcessed.current = false;
      return false;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // Detect embedded mode
    const isEmbedded = typeof window !== "undefined" && window.parent !== window;
    setEmbedded(isEmbedded);

    // GLOBAL SSO INTERCEPTOR: Check for ?sso_token= on ANY route
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso_token");

    if (ssoToken) {
      // Strip token from URL immediately (security)
      window.history.replaceState(null, "", window.location.pathname);
      const ok = await processSSOToken(ssoToken);
      if (ok) return;
    }

    // Normal auth check
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      // If embedded and no auth, request token from Hub via postMessage
      if (isEmbedded) {
        try {
          window.parent.postMessage({ type: "cargodash.token.refresh" }, "*");
        } catch {}
      }
    } finally {
      setLoading(false);
    }
  }, [processSSOToken]);

  // Listen for postMessage from Hub (token delivery + refresh)
  useEffect(() => {
    const handleMessage = async (event) => {
      // Validate origin
      if (!HUB_ORIGINS.includes(event.origin)) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "cargodash.token" && data.token) {
        const ok = await processSSOToken(data.token);
        if (!ok) {
          // Notify Hub of failure
          try {
            window.parent.postMessage({ type: "cargodash.token.error", error: "Token validation failed" }, event.origin);
          } catch {}
        }
      } else if (data.type === "cargodash.token.error") {
        // Hub session is dead — clear local session, redirect to Hub login
        setUser(null);
        setLoading(false);
        const appUrl = encodeURIComponent(window.location.origin);
        window.top.location.href = `${event.origin}/login?return_to=${appUrl}`;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [processSSOToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, formatApiError, embedded }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
